import { memo, useMemo } from 'react'
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts'
import { Box } from '@mui/material'

import { colors } from 'theme/colors'
import {
  ACTIVITY_CATEGORY_ICONS_MAP,
  ACTIVITY_COLORS,
} from 'ui/clinicianScreens/Patient/Report/sections/Activity/consts'
import { getActivityCategoryChartData } from 'ui/clinicianScreens/Patient/Report/sections/Activity/helpers'
import { DEBOUNCE_DELAY_DEFAULT } from 'ui/baseComponents/Charts/consts'
import { useReportTranslation } from 'utilities/i18n'

const ReportChartActivityCategories = ({
  activityDurationsByCategory = [],
}) => {
  const { t } = useReportTranslation()

  const STYLES = useMemo(
    () => ({
      chartContainer: {
        width: '100%',
        '& .recharts-legend-item-text': {
          color: `${colors.GREY[700]} !important`, // overriding js
          verticalAlign: 'middle',
        },
        '& .recharts-legend-wrapper': {
          '&::before': {
            content: `"${t('report.activity.chartTimeInCategory')}"`,
            color: ACTIVITY_COLORS.time,
            textTransform: 'uppercase',
            fontWeight: 'bold',
          },
        },
      },
    }),
    [t],
  )

  const data = getActivityCategoryChartData(activityDurationsByCategory) || []

  const barColors = {
    recent: colors.PRIMARY[1000],
    previous: colors.PRIMARY[400],
  }

  const legendLabels = useMemo(
    () => ({
      recent: t('report.chart.periodRecent'),
      previous: t('report.chart.periodPrevious'),
    }),
    [t],
  )

  const renderCustomAxisTick = ({ x, y, payload }) => {
    const slug = payload?.payload?.slug

    return (
      <g>
        <image
          href={ACTIVITY_CATEGORY_ICONS_MAP.get(slug)}
          width="20"
          height="20"
          x={x + 8}
          y={y}
        />
        <text x={x + 32} y={y + 14} fontSize={12}>
          {payload.value}
        </text>
      </g>
    )
  }

  const hasActivityDuration = data?.length > 0

  return hasActivityDuration ? (
    <Box
      sx={STYLES.chartContainer}
      data-chart-id="activity-category-chart"
      data-cy="activity-category-chart"
    >
      <ResponsiveContainer
        debounce={DEBOUNCE_DELAY_DEFAULT}
        width="100%"
        height={200}
      >
        <RechartsBarChart
          {...{
            data,
            width: 600,
            height: 200,
            margin: {
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
            },
          }}
        >
          <CartesianGrid strokeDasharray="0" stroke={colors.TREMOR[100]} />
          <XAxis
            dataKey="name"
            type="category"
            scale="band"
            tick={renderCustomAxisTick}
            tickLine={{ stroke: colors.TREMOR[100] }}
            axisLine={{ stroke: colors.TREMOR[100] }}
          />
          <YAxis
            unit="m"
            type="number"
            tickLine={{ stroke: colors.TREMOR[100] }}
            axisLine={{ stroke: colors.TREMOR[100] }}
            fontSize={12}
          />
          <Legend
            verticalAlign="top"
            align="left"
            iconType="square"
            iconSize={10}
            wrapperStyle={{
              top: -8,
              left: 60,
              fontSize: 12,
              textTransform: 'capitalize',
            }}
            payload={Object.keys(barColors).map((key) => ({
              value: legendLabels[key],
              type: 'square',
              color: barColors[key],
            }))}
          />
          <Bar
            {...{
              dataKey: 'recent',
              fill: barColors.recent,
              radius: [5, 5, 0, 0],
              isAnimationActive: false,
            }}
          />
          <Bar
            {...{
              dataKey: 'previous',
              fill: barColors.previous,
              radius: [5, 5, 0, 0],
              isAnimationActive: false,
            }}
          />
        </RechartsBarChart>
      </ResponsiveContainer>
    </Box>
  ) : null
}

export default memo(ReportChartActivityCategories)
